import React, { useEffect, useState } from "react";
import { docsApi } from "../lib/api";
import { currentDealKey } from "../lib/dealctx";
import { FolderOpen, Paperclip, X, History } from "lucide-react";
import { toast } from "sonner";

export const docLine = (d) => `${d.label}${d.version > 1 ? ` · v${d.version}` : ""} · ${d.size < 1024 ? "<1 KB" : `${(d.size / 1024).toFixed(0)} KB`} · ${String(d.uploadedAt || "").slice(0, 10)}`;

// Pick documents out of the Docs Room and hand them back as base64 attachments.
export const DocPickerModal = ({ open, onClose, dealKey, onAttach }) => {
  const key = dealKey || currentDealKey();
  const [docs, setDocs] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showOld, setShowOld] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel({}); setLoading(true);
    docsApi.list(key ? { dealKey: key } : {}).then((r) => setDocs(r.docs || [])).catch(() => setDocs([])).finally(() => setLoading(false));
  }, [open, key]);

  if (!open) return null;
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const newest = new Map();
  docs.forEach((d) => {
    const k = `${d.dealKey}|${d.category}|${d.label}`;
    if (!newest.has(k) || (d.version || 0) > (newest.get(k).version || 0)) newest.set(k, d);
  });
  const latestIds = new Set([...newest.values()].map((d) => d.id));
  const olderCount = docs.length - latestIds.size;
  const list = showOld ? docs : docs.filter((d) => latestIds.has(d.id));

  const attach = async () => {
    setBusy(true);
    try {
      const files = await Promise.all(ids.map((id) => docsApi.base64(id)));
      onAttach(files.map((f) => ({ filename: f.filename, content: f.content, label: f.label, size: f.size })));
      toast.success(`Attached ${files.length} document${files.length > 1 ? "s" : ""} from the Docs Room`);
      onClose();
    } catch { toast.error("Could not read those documents"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" data-testid="docpicker-modal" onMouseDown={onClose} style={{ zIndex: 90 }}>
      <div className="modal-card" style={{ maxWidth: 620, width: "94%", maxHeight: "86vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}><FolderOpen size={14} style={{ marginRight: 6 }} />Attach from Docs Room</div>
          <button className="btn btn-sm" onClick={onClose} data-testid="docpicker-close"><X size={14} /></button>
        </div>
        {loading ? <div className="ir-fine">Loading the library…</div>
          : docs.length === 0 ? <div className="ir-fine" data-testid="docpicker-empty">Nothing in the Docs Room yet — upload teasers, decks, financials or tax returns on the Docs Room tab.</div>
            : (
              <>
                <div className="doc-pick-list">
                  {list.map((d) => (
                    <label key={d.id} className={`doc-pick${sel[d.id] ? " on" : ""}${latestIds.has(d.id) ? "" : " doc-row-old"}`} data-testid={`docpick-${d.id}`}>
                      <input type="checkbox" checked={!!sel[d.id]} onChange={(e) => setSel((s) => ({ ...s, [d.id]: e.target.checked }))} data-testid={`docpick-cb-${d.id}`} />
                      <span className="doc-pick-main">
                        <b>{d.label}{d.version > 1 ? <em className="doc-ver">v{d.version}</em> : null}</b>
                        <span>{d.category} · {d.filename} · {d.size < 1024 ? "<1 KB" : `${(d.size / 1024).toFixed(0)} KB`} · {String(d.uploadedAt || "").slice(0, 10)}{d.dealKey === "firm" ? " · firm-wide" : ""}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {olderCount ? (
                  <button className="doc-older-toggle" onClick={() => setShowOld((v) => !v)} data-testid="docpicker-older">
                    <History size={11} />{showOld ? "Newest versions only" : `Show ${olderCount} older version${olderCount > 1 ? "s" : ""}`}
                  </button>
                ) : null}
              </>
            )}
        <button className="btn btn-primary" style={{ width: "100%", marginTop: 12 }} disabled={!ids.length || busy} onClick={attach} data-testid="docpicker-attach">
          <Paperclip size={13} style={{ marginRight: 6 }} />{busy ? "Attaching…" : `Attach ${ids.length || ""} selected`}
        </button>
      </div>
    </div>
  );
};
